import type { LocateCommandType, LocateSettings } from '../types/RobotCleaningControl.types';
import { LOCATE_SETTLE_MS } from './constants';
import { Cv50Commands } from 'app/cross-cutting-concerns/communication/interfaces/pra-api-graphql';

interface SettlePin {
  isOn: boolean;
  until: number;
}

// Keyed by robot, so a pin never holds back another robot's reports and survives a reconnect.
const pins = new Map<string, SettlePin>();

const pinKey = (robotKey: string, commandType: LocateCommandType): string => `${robotKey}|${commandType}`;

/** Keeps a confirmed Beep / Blink state against contradicting device reports for a short while. */
export const pinLocateSetting = (
  robotKey: string,
  commandType: LocateCommandType,
  isOn: boolean,
  nowMs = Date.now()
): void => {
  pins.set(pinKey(robotKey, commandType), { isOn, until: nowMs + LOCATE_SETTLE_MS });
};

export const unpinLocateSetting = (robotKey: string, commandType: LocateCommandType): void => {
  pins.delete(pinKey(robotKey, commandType));
};

const dropIfContradicted = (
  robotKey: string,
  commandType: LocateCommandType,
  isOn: boolean | undefined,
  nowMs: number
): boolean | undefined => {
  const key = pinKey(robotKey, commandType);
  const pin = pins.get(key);

  if (!pin || nowMs >= pin.until) {
    pins.delete(key);

    return isOn;
  }

  return isOn === pin.isOn ? isOn : undefined;
};

/** The reported settings without those that contradict a pinned (just confirmed) state. */
export const dropSettledLocateContradictions = (
  robotKey: string,
  { beep, blink }: LocateSettings,
  nowMs = Date.now()
): LocateSettings => ({
  beep: dropIfContradicted(robotKey, Cv50Commands.Beep, beep, nowMs),
  blink: dropIfContradicted(robotKey, Cv50Commands.Blink, blink, nowMs),
});

export const getRobotKey = (materialNumber: string, serialNumber: string): string =>
  `${materialNumber}:${serialNumber}`;
